import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Button, Modal, Form } from 'react-bootstrap';
import { filesApi } from '../../api';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminFiles.css';

const AdminFiles = () => {
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadType, setUploadType] = useState('IMAGE');
    const [uploadProgress, setUploadProgress] = useState(0);

    const [deleteId, setDeleteId] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const loadFiles = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await filesApi.list({ page: currentPage - 1, size: pageSize });
            const list = Array.isArray(data) ? data : (data?.content || []);
            const total = Array.isArray(data) ? data.length : (data?.totalElements || data?.total || list.length);
            setFileList(list);
            setTotalItems(total);
        } catch (err) {
            console.error('[AdminFiles] Failed to load files:', err);
            setFileList([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        loadFiles();
    }, [loadFiles]);

    const handleUpload = async () => {
        if (!uploadFile) {
            setError('Выберите файл');
            return;
        }

        try {
            setActionLoading(true);
            setUploadProgress(0);
            const response = await filesApi.upload(uploadFile, { type: uploadType }, (progressEvent) => {
                const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                setUploadProgress(percent);
            });
            // response содержит { message, file: { ... } }
            const fileData = response?.file || response;
            console.info('[AdminFiles] File uploaded:', fileData);
            setShowUploadModal(false);
            setUploadFile(null);
            setUploadProgress(0);
            await loadFiles();
        } catch (err) {
            setError(`Не удалось загрузить файл: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId.trim()) {
            setError('Введите ID файла');
            return;
        }
        try {
            setActionLoading(true);
            await filesApi.remove(deleteId.trim());
            setShowDeleteModal(false);
            setDeleteId('');
            await loadFiles();
        } catch (err) {
            setError(`Не удалось удалить файл: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const formatSize = (bytes) => {
        if (!bytes) return '-';
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(1)} KB`;
        return `${(kb / 1024).toFixed(2)} MB`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('ru-RU');
    };

    if (loading && fileList.length === 0) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Управление файлами</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <HrnbCard>
                <div className="admin-files-actions">
                    <Button variant="primary" onClick={() => setShowUploadModal(true)} className="me-2">
                        📤 Загрузить файл
                    </Button>
                    <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>
                        🗑️ Удалить по ID
                    </Button>
                </div>

                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value={5}>5 на странице</option>
                        <option value={10}>10 на странице</option>
                        <option value={25}>25 на странице</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-files-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Имя файла</th>
                            <th>Тип</th>
                            <th>Размер</th>
                            <th>Владелец</th>
                            <th>Дата загрузки</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fileList.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">
                                    {error ? 'Ошибка загрузки списка файлов' : 'Файлы не найдены'}
                                </td>
                            </tr>
                        ) : (
                            fileList.map((file) => (
                                <tr key={file.id}>
                                    <td>{file.id}</td>
                                    <td>
                                        <strong>{file.name || file.fileName || '—'}</strong>
                                        {file.url && (
                                            <div className="small">
                                                <a href={file.url} target="_blank" rel="noopener noreferrer">
                                                    Открыть
                                                </a>
                                            </div>
                                        )}
                                    </td>
                                    <td>{file.type || file.fileType || '—'}</td>
                                    <td>{formatSize(file.size || file.fileSize)}</td>
                                    <td>{file.owner?.username || file.owner || file.userId || '—'}</td>
                                    <td>{formatDate(file.createdAt || file.uploadedAt)}</td>
                                    <td>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => { setDeleteId(String(file.id)); setShowDeleteModal(true); }}
                                            disabled={actionLoading}
                                        >
                                            Удалить
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Upload Modal */}
            <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Загрузка файла</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Тип файла</Form.Label>
                            <Form.Select value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
                                <option value="IMAGE">Изображение</option>
                                <option value="AUDIO">Аудио</option>
                                <option value="VIDEO">Видео</option>
                                <option value="DOCUMENT">Документ</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Файл</Form.Label>
                            <Form.Control
                                type="file"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                            />
                        </Form.Group>
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="admin-files-progress">
                                <div className="progress">
                                    <div
                                        className="progress-bar"
                                        role="progressbar"
                                        style={{ width: `${uploadProgress}%` }}
                                    >
                                        {uploadProgress}%
                                    </div>
                                </div>
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleUpload} disabled={actionLoading || !uploadFile}>
                        {actionLoading ? 'Загрузка...' : 'Загрузить'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Удаление файла</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>ID файла для удаления</Form.Label>
                        <Form.Control
                            type="text"
                            value={deleteId}
                            onChange={(e) => setDeleteId(e.target.value)}
                            placeholder="Введите ID"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={actionLoading}>
                        {actionLoading ? 'Удаление...' : 'Удалить'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminFiles;
